<template>
  <TransitionRoot appear :show="isShown" as="template">
    <Dialog as="div" @close="() => {}" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50" aria-hidden="true" />
      </TransitionChild>

      <div class="fixed inset-0 w-screen">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-lg transform rounded-lg bg-white text-left align-middle shadow-xl transition-all flex flex-col max-h-[90vh]"
            >
              <SpinnerLoader v-if="isSubmitting" />

              <div
                class="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white sticky top-0 z-10"
              >
                <h3 class="text-lg font-semibold text-gray-900">
                  {{ $t('resources.form.title.' + type) }}
                </h3>
                <button
                  @click="$emit('close')"
                  class="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 cursor-pointer"
                >
                  <Icon icon="mdi:close" class="w-6 h-6" />
                </button>
              </div>

              <div class="p-6 overflow-y-auto">
                <form @submit.prevent="submitForm" id="resource-form" class="flex flex-col gap-5">
                  <fieldset :disabled="isSubmitting" class="contents disabled:cursor-default">
                    <div class="flex flex-col gap-1">
                      <BaseSelect
                        id="type"
                        v-model="form.type.value.value"
                        :label="$t('resources.form.fields.type.label') + ' *'"
                        :options="Object.values(ResourceType)"
                        :getLabel="(item) => $t('resources.resourceType.' + item)"
                        :getValue="(item) => item"
                        :error="form.type.errorMessage.value"
                        @blur="form.type.handleChange(form.type.value.value)"
                      />
                    </div>

                    <div class="flex flex-col gap-1" v-if="otherRessourceTypeIsSelected">
                      <BaseInput
                        id="otherType"
                        :label="$t('resources.form.fields.otherType.label')"
                        v-model="form.otherType.value.value"
                        :placeholder="$t('resources.form.fields.otherType.placeholder')"
                        :showErrorMessage="true"
                        :error="form.otherType.errorMessage.value"
                        @blur="form.otherType.handleChange"
                        width="w-full"
                      />
                    </div>

                    <div class="flex flex-col gap-1">
                      <label class="text-sm font-medium text-gray-700">
                        {{ $t('resources.form.fields.imagePreview.label') }}
                      </label>
                      <ImagesLoader
                        @updateFiles="handleImagePreviewUpdate"
                        :existingImages="existingImagePreview"
                        :uniqueImage="true"
                        :externalImagesLoader="false"
                      />
                    </div>

                    <div
                      class="flex flex-col gap-1"
                      v-if="form.type.value.value === ResourceType.EVENTS"
                    >
                      <label class="text-sm font-medium text-gray-700 required">
                        {{ $t('resources.form.fields.date.label') }}
                      </label>
                      <DateInput
                        v-model:start-at="form.startAt.value.value"
                        v-model:end-at="form.endAt.value.value"
                        :error-messages="
                          form.startAt.errorMessage.value || form.endAt.errorMessage.value
                        "
                        @change="handleDateChange"
                      />
                      <p
                        v-if="form.startAt.errorMessage.value || form.endAt.errorMessage.value"
                        class="text-xs text-red-500 mt-1"
                      >
                        {{ form.startAt.errorMessage.value || form.endAt.errorMessage.value }}
                      </p>
                    </div>

                    <div class="flex flex-col gap-1">
                      <BaseInput
                        id="name"
                        :label="$t('resources.form.fields.name.label')"
                        v-model="form.name.value.value"
                        :placeholder="$t('resources.form.fields.name.label')"
                        :showErrorMessage="true"
                        :error="form.name.errorMessage.value"
                        @blur="form.name.handleChange"
                        width="w-full"
                        required
                      />
                    </div>

                    <div class="flex flex-col gap-1">
                      <label for="description" class="text-sm font-medium text-gray-700 required">
                        {{ $t('resources.form.fields.description.label') }}
                      </label>
                      <textarea
                        id="description"
                        v-model="form.description.value.value"
                        @blur="form.description.handleChange"
                        :placeholder="$t('resources.form.fields.description.label')"
                        rows="4"
                        class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-main focus:outline-none focus:ring-1 focus:ring-main disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500 transition-colors"
                        :class="{ 'border-red-500': form.description.errorMessage.value }"
                      ></textarea>
                      <p
                        v-if="form.description.errorMessage.value"
                        class="text-xs text-red-500 mt-1"
                      >
                        {{ form.description.errorMessage.value }}
                      </p>
                    </div>

                    <div class="flex flex-col gap-1">
                      <BaseInput
                        id="author"
                        :label="$t('resources.form.fields.author.label')"
                        v-model="form.author.value.value"
                        :placeholder="$t('resources.form.fields.author.placeholder')"
                        :showErrorMessage="true"
                        :error="form.author.errorMessage.value"
                        @blur="form.author.handleChange"
                        width="w-full"
                        required
                      />
                    </div>

                    <div class="border-t border-gray-200 my-2"></div>
                    <FormSectionTitle :text="$t('resources.form.section.resource')" />

                    <div class="flex flex-col gap-1">
                      <BaseSelect
                        id="format"
                        v-model="form.format.value.value"
                        :label="$t('resources.form.fields.format.label') + ' *'"
                        :options="resourceFormats"
                        :getLabel="(item) => $t('resources.resourceFormat.' + item)"
                        :getValue="(item) => item"
                        :error="form.format.errorMessage.value"
                        @blur="form.format.handleChange(form.format.value.value)"
                      />
                    </div>

                    <div class="flex flex-col gap-1" v-if="form.format.value.value">
                      <BaseInput
                        id="link"
                        :label="$t('resources.form.fields.link.label')"
                        v-model="form.link.value.value"
                        :placeholder="$t('resources.form.fields.link.placeholder')"
                        :showErrorMessage="true"
                        :error="form.link.errorMessage.value"
                        @blur="form.link.handleChange"
                        width="w-full"
                      />
                    </div>

                    <div v-if="!hideFileInput && form.format.value.value" class="relative my-2">
                      <div class="absolute inset-0 flex items-center" aria-hidden="true">
                        <div class="w-full border-t border-gray-200"></div>
                      </div>
                      <div class="relative flex justify-center">
                        <span class="bg-white px-2 text-sm text-gray-500">{{
                          $t('forms.or')
                        }}</span>
                      </div>
                    </div>

                    <div
                      class="flex flex-col gap-1"
                      v-if="!hideFileInput && form.format.value.value"
                    >
                      <label class="text-sm font-medium text-gray-700">
                        {{ $t('resources.form.fields.file.label') }}
                      </label>
                      <FileInput
                        v-model="form.file.value.value"
                        :error-messages="form.file.errorMessage.value"
                        @update:model-value="form.file.handleChange(form.file.value.value)"
                      />
                      <p v-if="form.file.errorMessage.value" class="text-xs text-red-500 mt-1">
                        {{ form.file.errorMessage.value }}
                      </p>
                    </div>

                    <template v-if="showAdminScope">
                      <div class="border-t border-gray-200 my-2"></div>
                      <FormSectionTitle :text="$t('actors.form.adminScopeSection')" />

                      <div class="flex flex-col gap-1">
                        <BaseSelect
                          id="administrativeScopes"
                          v-model="form.administrativeScopes.value.value"
                          :label="$t('actors.form.adminScope')"
                          :options="Object.values(AdministrativeScope)"
                          :getLabel="(item) => $t('actors.scope.' + item)"
                          :getValue="(item) => item"
                          :error="form.administrativeScopes.errorMessage.value"
                          @blur="
                            form.administrativeScopes.handleChange(
                              form.administrativeScopes.value.value,
                            )
                          "
                          multiple
                        />
                      </div>

                      <Autocomplete
                        v-if="activeAdminLevels && activeAdminLevels.admin1"
                        v-model="form.admin1List.value.value"
                        :label="$t('actors.form.admin1')"
                        :items="adminBoundariesStore.admin1Boundaries"
                        :item-title="(item: any) => item.adm1Name"
                        :item-value="(item: any) => item"
                        multiple
                      />

                      <Autocomplete
                        v-if="activeAdminLevels && activeAdminLevels.admin3"
                        v-model="form.admin3List.value.value"
                        :label="$t('actors.form.admin3')"
                        :items="adminBoundariesStore.admin3Boundaries"
                        :item-title="(item: any) => item.adm3Name"
                        :item-value="(item: any) => item"
                        multiple
                      />
                      <div class="border-t border-gray-200 my-2"></div>
                    </template>

                    <div class="bg-gray-50 p-4 rounded-lg border border-gray-100">
                      <label class="block text-sm font-semibold text-gray-900 mb-3">
                        {{ $t('forms.banoc.title') }}
                      </label>
                      <div class="flex gap-4">
                        <div class="w-1/3 flex flex-col gap-1">
                          <BaseInput
                            id="banoc"
                            :label="$t('forms.banoc.code')"
                            v-model="form.banoc.value.value"
                            :showErrorMessage="true"
                            :error="form.banoc.errorMessage.value"
                            @blur="form.banoc.handleChange"
                            width="w-full"
                          />
                        </div>
                        <div class="w-2/3 flex flex-col gap-1">
                          <BaseInput
                            id="banocUrl"
                            :label="$t('forms.banoc.url')"
                            v-model="form.banocUrl.value.value"
                            :showErrorMessage="true"
                            :error="form.banocUrl.errorMessage.value"
                            @blur="form.banocUrl.handleChange"
                            width="w-full"
                          />
                        </div>
                      </div>
                    </div>

                    <template v-if="showLocation || showAdminScope">
                      <FormSectionTitle
                        v-if="showLocation"
                        :text="$t('resources.form.section.location')"
                      />
                      <label class="text-sm font-medium text-gray-700" v-if="showAdminScope">
                        {{ $t('resources.form.section.centroids') }}
                      </label>
                      <LocationSelector
                        @update:model-value="form.geoData.handleChange"
                        v-model="form.geoData.value.value"
                        :error-message="form.geoData.errorMessage.value"
                      />
                    </template>

                    <div class="border-t border-gray-200 my-2"></div>

                    <FormSectionTitle :text="$t('resources.form.section.thematics')" />
                    <div class="flex flex-col gap-1">
                      <BaseSelect
                        id="thematics"
                        v-model="form.thematics.value.value"
                        :label="$t('resources.form.fields.thematics.subLabel') + ' *'"
                        :options="Object.values(Thematic)"
                        :getLabel="(item) => $t('forms.thematics.' + item)"
                        :getValue="(item) => item"
                        :error="form.thematics.errorMessage.value"
                        @blur="form.thematics.handleChange(form.thematics.value.value)"
                        multiple
                      />
                    </div>

                    <div class="flex flex-col gap-1" v-if="otherThematicIsSelected">
                      <BaseInput
                        id="otherThematic"
                        :label="$t('resources.form.fields.otherThematics.label')"
                        v-model="form.otherThematic.value.value"
                        :placeholder="$t('resources.form.fields.otherThematics.placeholder')"
                        :showErrorMessage="true"
                        :error="form.otherThematic.errorMessage.value"
                        @blur="form.otherThematic.handleChange"
                        width="w-full"
                      />
                    </div>

                    <div class="flex flex-col gap-1">
                      <BaseSelect
                        id="odds"
                        v-model="form.odds.value.value"
                        :label="$t('forms.odds.title') + ' *'"
                        :options="Object.values(ODD)"
                        :getLabel="(item) => $t('forms.odds.' + item)"
                        :getValue="(item) => item"
                        :error="form.odds.errorMessage.value"
                        @blur="form.odds.handleChange(form.odds.value.value)"
                        multiple
                      />
                    </div>
                  </fieldset>
                </form>
              </div>

              <div
                class="px-6 py-4 flex justify-between items-center border-t border-gray-200 bg-gray-50"
              >
                <div class="flex items-center gap-3">
                  <button
                    type="button"
                    class="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-900 hover:underline transition-colors"
                    @click="$emit('close')"
                  >
                    {{ $t('forms.cancel') }}
                  </button>
                  <span v-show="isSubmitting" class="text-sm text-amber-500">{{
                    $t('forms.submitting')
                  }}</span>
                </div>

                <button
                  type="submit"
                  form="resource-form"
                  :disabled="isSubmitting"
                  class="inline-flex justify-center rounded-md bg-main hover:bg-main-hover px-6 py-2.5 text-sm font-medium text-white focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-default transition-colors items-center"
                >
                  <Icon v-if="isSubmitting" icon="eos-icons:loading" class="mr-2 h-4 w-4" />
                  {{ submitLabel }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from '@headlessui/vue'
import { Icon } from '@iconify/vue'
import DateInput from '@/components/forms/DateInput.vue'
import FileInput from '@/components/forms/FileInput.vue'
import ImagesLoader from '@/components/forms/ImagesLoader.vue'
import LocationSelector from '@/components/forms/LocationSelector.vue'
import SpinnerLoader from '@/components/ui/SpinnerLoader.vue'
import BaseInput from '@/components/ui/BaseInput.vue'
import BaseSelect from '@/components/ui/BaseSelect.vue'
import Autocomplete from '@/components/ui/Autocomplete.vue'
import FormSectionTitle from '@/components/text-elements/FormSectionTitle.vue'
import { AdministrativeScope } from '@/models/enums/AdministrativeScope'
import { FormType } from '@/models/enums/app/FormType'
import { NotificationType } from '@/models/enums/app/NotificationType'
import { ODD } from '@/models/enums/contents/ODD'
import { ResourceFormat } from '@/models/enums/contents/ResourceFormat'
import { ResourceType } from '@/models/enums/contents/ResourceType'
import { Thematic } from '@/models/enums/contents/Thematic'
import type { ContentImageFromUserFile } from '@/models/interfaces/ContentImage'
import type { FileObject } from '@/models/interfaces/object/FileObject'
import { type Resource } from '@/models/interfaces/Resource'
import { i18n } from '@/plugins/i18n'
import { nestedObjectsToIri } from '@/services/api/ApiPlatformService'
import { onInvalidSubmit } from '@/services/forms/FormService'
import { addNotification } from '@/services/notifications/NotificationService'
import { ResourceFormService } from '@/services/resources/ResourceFormService'
import { useAdminBoundariesStore } from '@/stores/adminBoundariesStore'
import { useResourceStore } from '@/stores/resourceStore'
import type { Ref } from 'vue'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const resourceStore = useResourceStore()
const adminBoundariesStore = useAdminBoundariesStore()

const { t } = useI18n()

const props = defineProps<{
  type: FormType
  resource: Resource | null
  isShown: boolean
}>()

onMounted(async () => {
  await Promise.all([adminBoundariesStore.getAdmin1(), adminBoundariesStore.getAdmin3()])
  existingImagePreview.value = props.resource?.previewImage ? [props.resource.previewImage] : []
})

const { form, handleSubmit, isSubmitting } = ResourceFormService.getForm(props.resource)
const submitLabel = computed(() => {
  /* if (userStore.userIsAdmin() && props.type === FormType.CREATE) {
    return t('forms.continue')
  } */
  return t('forms.' + props.type)
})

const showAdminScope = computed(() => {
  return [ResourceType.RAPPORTS, ResourceType.REGULATIONS, ResourceType.OTHERS].includes(
    form.type.value.value,
  )
})
const activeAdminLevels = computed(() => {
  if (
    form.administrativeScopes.value?.value &&
    Array.isArray(form.administrativeScopes.value?.value)
  ) {
    return {
      admin1: (form.administrativeScopes.value?.value as AdministrativeScope[]).includes(
        AdministrativeScope.REGIONAL,
      ),
      admin3: (form.administrativeScopes.value?.value as AdministrativeScope[]).includes(
        AdministrativeScope.CITY,
      ),
    }
  }
  return false
})

const otherRessourceTypeIsSelected = computed(() => {
  if (form.type.value?.value) {
    return form.type.value?.value === ResourceType.OTHERS
  }
  return false
})
const otherThematicIsSelected = computed(() => {
  if (form.thematics.value?.value && Array.isArray(form.thematics.value?.value)) {
    return (form.thematics.value?.value as Thematic[]).includes(Thematic.OTHERS)
  }
  return false
})

const existingImagePreview = ref<(FileObject | string)[]>([])

const newImagePreview: Ref<ContentImageFromUserFile[]> = ref([])
const handleImagePreviewUpdate = (list: any) => {
  newImagePreview.value = list.selectedFiles
}

const hideFileInput = computed(() => {
  if (!form.format.value.value) return false
  return [ResourceFormat.VIDEO, ResourceFormat.WEB].includes(form.format.value.value)
})

const handleDateChange = () => {
  form.startAt.handleChange(form.startAt.value.value)
  form.endAt.handleChange(form.endAt.value.value)
}

const emit = defineEmits(['submitted', 'close'])
watch(
  () => form.type.value.value,
  () => {
    if (!resourceFormats.value.includes(form.format.value.value)) {
      form.format.value.value = null
    }
  },
)
const showLocation = computed(() => {
  return [ResourceType.EVENTS, ResourceType.GUIDES, ResourceType.OTHERS].includes(
    form.type.value.value,
  )
})
const resourceFormats = computed(() => {
  switch (form.type.value.value) {
    case ResourceType.EVENTS:
      return [ResourceFormat.WEB, ResourceFormat.PDF, ResourceFormat.IMAGE, ResourceFormat.VIDEO]
    case ResourceType.GUIDES:
      return [
        ResourceFormat.WEB,
        ResourceFormat.PDF,
        ResourceFormat.IMAGE,
        ResourceFormat.VIDEO,
        ResourceFormat.XLSX,
      ]
    case ResourceType.RAPPORTS:
    case ResourceType.REGULATIONS:
    default:
      return [ResourceFormat.WEB, ResourceFormat.PDF, ResourceFormat.IMAGE]
  }
})

const submitForm = handleSubmit(
  async (values) => {
    const resourceSubmission: Resource = nestedObjectsToIri(values)
    if ([FormType.EDIT, FormType.VALIDATE].includes(props.type) && props.resource) {
      resourceSubmission.id = props.resource.id
    }
    resourceSubmission.previewImageToUpload = newImagePreview.value[0] ?? null
    resourceSubmission.previewImage = props.resource?.previewImage

    const submittedResource = await resourceStore.submitResource(resourceSubmission, props.type)
    emit('submitted', submittedResource)
  },
  (errors) => {
    console.error('Resource form submission errors:', errors)
    addNotification(i18n.t('forms.errors'), NotificationType.ERROR)
    onInvalidSubmit()
  },
)
</script>
